import type { Vote } from '@nrk/pin-votes-schemas/vote-post';
import { HttpError, parseRetryAfterHeader } from '@pin/client-http-utils';
import { forceRefresh, loginSessionStore } from '../login/loginSessionStore';
import { get } from 'svelte/store';
import { trackEvent } from '../util/snowplow';
import { captureError, captureHttpError } from '../util/sentry';

const postvote = async (url: string, vote: Vote): Promise<Vote> => {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), 2_000);
  const accessToken = get(loginSessionStore)?.accessToken;

  if (accessToken == undefined) {
    // We just log this event if we know it's gonna fail.
    trackEvent({
      action: 'error:accesstoken-undefined',
    });
    const session = get(loginSessionStore);
    captureError(
      'error:accesstoken-undefined',
      new Error('error:accesstoken-undefined'),
      {
        isOffgrid: JSON.stringify(session?.isOffgrid),
        isExpired: JSON.stringify(session?.isExpired),
        expires:
          session != null
            ? new Date(session.expires).toLocaleString()
            : 'no data',
      }
    );
  }
  const response = await fetch(`${url}/api/vote`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(vote),
    signal: abortController.signal,
  });
  clearTimeout(timeoutId);
  if (!response.ok) {
    throw new HttpError(response);
  }
  return response.json();
};

export const postvoteWithRetries = async (
  url: string,
  vote: Vote,
  tryCount = 1
): Promise<Vote> => {
  try {
    return await postvote(url, vote);
  } catch (error) {
    if (
      tryCount === 1 && // first retry
      error instanceof HttpError &&
      error.response.status === 401 // unauthorized
    ) {
      await forceRefresh();
    }
    if (tryCount > 5) {
      if (error instanceof HttpError) {
        captureHttpError('post-vote:failed-http', error.response);
      } else {
        captureError('post-vote:failed', error);
      }

      throw error;
    }
    const exponentialBackoff = 1000 * tryCount ** 2;
    let retryAfter = 0;
    if (error instanceof HttpError) {
      retryAfter = parseRetryAfterHeader(error.response) ?? 0;
    }
    await delay(Math.max(retryAfter, exponentialBackoff));
    return postvoteWithRetries(url, vote, tryCount + 1);
  }
};

const delay = (waitTime = 0) => {
  return new Promise((resolve) => {
    setTimeout(resolve, waitTime);
  });
};
